import type { FormField } from '@lucid/lego';

export const createClaimFormFields = (): FormField[] => [
  // Claim Type
  {
    id: 1,
    fieldType: 'dropdown',
    name: 'claimType',
    apiKey: 'claimType',
    label: 'Claim Type',
    placeholder: 'Select claim type',
    required: true,
    width: 'half',
    options: [
      { label: 'Warranty', value: 'warranty' },
      { label: 'Goodwill', value: 'goodwill' },
      { label: 'Recall', value: 'recall' },
      { label: 'Campaign', value: 'campaign' },
    ],
    validationMessages: {
      required: 'Claim type is required',
    },
  },
  // Claim Title
  {
    id: 2,
    fieldType: 'text',
    name: 'claimTitle',
    apiKey: 'claimTitle',
    label: 'Claim Title',
    placeholder: 'Enter claim title',
    required: true,
    width: 'half',
    validationMessages: {
      required: 'Claim title is required',
    },
  },
  // FSE Case ID
  {
    id: 3,
    fieldType: 'text',
    name: 'fseCaseId',
    apiKey: 'fseCaseId',
    label: 'FSE Case ID',
    placeholder: 'Enter FSE case ID',
    width: 'half',
  },
  // VIN
  {
    id: 4,
    fieldType: 'text',
    name: 'vin',
    apiKey: 'vin',
    label: 'VIN (Vehicle Identification Number)',
    placeholder: 'Enter VIN',
    required: true,
    width: 'half',
    validationMessages: {
      required: 'VIN is required',
    },
  },
  // Lucid ID
  {
    id: 5,
    fieldType: 'text',
    name: 'lucidId',
    apiKey: 'lucidId',
    label: 'Lucid ID',
    placeholder: 'Enter Lucid ID',
    width: 'half',
  },
  // Mileage IN
  {
    id: 6,
    fieldType: 'text',
    name: 'mileageIn',
    apiKey: 'mileageIn',
    label: 'Mileage IN',
    placeholder: 'Enter mileage in',
    required: true,
    width: 'half',
    validationMessages: {
      required: 'Mileage IN is required',
    },
  },
  // Mileage OUT
  {
    id: 7,
    fieldType: 'text',
    name: 'mileageOut',
    apiKey: 'mileageOut',
    label: 'Mileage OUT',
    placeholder: 'Enter mileage out',
    required: true,
    width: 'half',
    validationMessages: {
      required: 'Mileage OUT is required',
    },
  },
  // Repair Start Date
  {
    id: 8,
    fieldType: 'date_picker',
    name: 'repairStartDate',
    apiKey: 'repairStartDate',
    label: 'Repair Start Date',
    placeholder: 'mm/dd/yyyy',
    required: true,
    width: 'half',
    validationMessages: {
      required: 'Repair start date is required',
    },
  },
  // Repair End Date
  {
    id: 9,
    fieldType: 'date_picker',
    name: 'repairEndDate',
    apiKey: 'repairEndDate',
    label: 'Repair End Date',
    placeholder: 'mm/dd/yyyy',
    required: true,
    width: 'half',
    validationMessages: {
      required: 'Repair end date is required',
    },
  },
  // Mobile Service
  {
    id: 10,
    fieldType: 'checkbox',
    name: 'mobileService',
    apiKey: 'mobileService',
    label: 'Mobile Service',
    width: 'full',
  },
  // Job Code Entry
  {
    id: 11,
    fieldType: 'dropdown',
    name: 'jobCode',
    apiKey: 'jobCode',
    label: 'Job Code Entry',
    placeholder: 'Select job code',
    required: true,
    width: 'half',
    options: [
      { label: 'Battery System Repair', value: 'battery_repair' },
      { label: 'Electrical System', value: 'electrical' },
      { label: 'DreamDrive Calibration', value: 'dreamdrive' },
      { label: 'HVAC Service', value: 'hvac' },
      { label: 'Suspension Repair', value: 'suspension' },
      { label: 'Body/Paint Work', value: 'body_paint' },
    ],
    validationMessages: {
      required: 'Job code is required',
    },
  },
  // Parts & Operation Code
  {
    id: 12,
    fieldType: 'text',
    name: 'opCode',
    apiKey: 'opCode',
    label: 'Parts & Operation Code (Op Code)',
    placeholder: 'Enter operation code',
    width: 'half',
  },
  // Causal Part Selection
  {
    id: 13,
    fieldType: 'text',
    name: 'causalPart',
    apiKey: 'causalPart',
    label: 'Causal Part Selection',
    placeholder: 'Link affected part number',
    width: 'full',
  },
  // Cause
  {
    id: 14,
    fieldType: 'text',
    name: 'cause',
    apiKey: 'cause',
    label: 'Cause',
    placeholder: 'Enter cause',
    width: 'third',
  },
  // Concern
  {
    id: 15,
    fieldType: 'text',
    name: 'concern',
    apiKey: 'concern',
    label: 'Concern',
    placeholder: 'Enter concern',
    width: 'third',
  },
  // Correction
  {
    id: 16,
    fieldType: 'text',
    name: 'correction',
    apiKey: 'correction',
    label: 'Correction',
    placeholder: 'Enter correction',
    width: 'third',
  },
  // Claim Description
  {
    id: 17,
    fieldType: 'text',
    name: 'claimDescription',
    apiKey: 'claimDescription',
    label: 'Claim Description',
    placeholder: 'Enter detailed claim description',
    required: true,
    width: 'full',
    validationMessages: {
      required: 'Claim description is required',
    },
  },
  // Photos & Videos Upload
  {
    id: 18,
    fieldType: 'file',
    name: 'photosVideos',
    apiKey: 'photosVideos',
    label: 'Photos & Videos',
    placeholder: 'Click to upload or drag and drop',
    helperText: 'PNG, JPG, GIF, MP4, MOV up to 10MB each',
    width: 'full',
    allowMultipleFiles: true,
  },
  // Supporting Documents
  {
    id: 19,
    fieldType: 'file',
    name: 'supportingDocuments',
    apiKey: 'supportingDocuments',
    label: 'Supporting Documents',
    placeholder: 'Click to upload or drag and drop',
    helperText: 'PDF, DOC, DOCX up to 5MB each',
    width: 'full',
    allowMultipleFiles: true,
  },
  // Signature / Acknowledgement
  {
    id: 20,
    fieldType: 'file',
    name: 'signature',
    apiKey: 'signature',
    label: 'Signature / Acknowledgement',
    placeholder: 'Click to upload or drag and drop',
    helperText: 'PNG, JPG, PDF up to 2MB',
    width: 'full',
  },
  // Submit Button
  {
    id: 21,
    fieldType: 'button',
    label: 'Submit Claim',
    color: 'brand',
    size: 'medium',
    type: 'primary',
    align: 'right',
  },
];
